/**
 * The per-tile editing menu.
 *
 * Rendered into `WidgetCard`'s `controls` slot, inside the drag handle — so it
 * carries `DRAG_CANCEL_CLASS`, or pressing any of these would start a drag
 * instead of opening the menu.
 *
 * ONE ENTRANCE FOR A TILE'S SETTINGS: **Configure…** opens a panel — the
 * title, the report (pick one, build one), the filters and the chart for a
 * tile that asks a question; the title and text for a note. None of those is
 * repeated here — a second way to reach a setting is a second place for the
 * two to disagree, and a field in a menu gives no sign of when it has been
 * taken. A custom report's own question is changed on its own page; the panel
 * links there. A tombstone has nothing to configure, only to remove.
 */
import type { DashboardTile, VizKind } from '@clockwise/semantic-core';
import { isDashboardReport } from '@clockwise/semantic-core';
import { FileOutput, MoreHorizontal, SlidersHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DRAG_CANCEL_CLASS } from '../dashboard-grid';

/** What each viz is called on the controls. Mirrors report-kit's own labels. */
export const VIZ_LABELS: Record<VizKind, string> = {
  table: 'Table',
  bar: 'Bar',
  line: 'Line',
  area: 'Area',
  pie: 'Pie',
  metric: 'Number',
};

interface Props {
  tile: DashboardTile;
  /** Open the tile's settings panel. */
  onConfigure?: () => void;
  /**
   * May the viewer save custom reports (`report.edit`)? Gates "Save as custom
   * report", which creates one.
   */
  mayEditReport?: boolean;
  /** Dashboard reports: save as a custom report. */
  onPromote?: () => void;
  onRemove: () => void;
}

export function TileEditor({
  tile,
  onConfigure,
  mayEditReport = false,
  onPromote,
  onRemove,
}: Props) {
  const configurable = tile.type === 'report' || tile.type === 'metric' || tile.type === 'text';

  return (
    <div className={`${DRAG_CANCEL_CLASS} flex items-center gap-1`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="size-7" aria-label="Tile options">
            <MoreHorizontal className="size-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {configurable && onConfigure && (
            <DropdownMenuItem onSelect={onConfigure}>
              <SlidersHorizontal className="size-4" aria-hidden="true" />
              Configure…
            </DropdownMenuItem>
          )}

          {isDashboardReport(tile) && mayEditReport && onPromote && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onPromote}>
                <FileOutput className="size-4" aria-hidden="true" />
                Save as custom report…
              </DropdownMenuItem>
            </>
          )}

          {configurable && onConfigure && <DropdownMenuSeparator />}
          <DropdownMenuItem variant="destructive" onSelect={onRemove}>
            <Trash2 className="size-4" aria-hidden="true" />
            Remove tile
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
